import { useState } from "react";
import { useNavigate } from "react-router";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { usePermissions } from "@/hooks/use-permissions";
import {
  modeLabel,
  useDatasheets,
  useDatasheetTypes,
  type DatasheetRow,
} from "@/lib/datasheets";

// Labels are prefixed so they never collide, in the DOM, with a row's mode badge —
// the badge renders modeLabel(mode) verbatim (e.g. "Prepaid"), and tests query for
// that text with a plain getByText, which requires a single match document-wide.
const MODES = [
  { value: "", label: "All modes" },
  { value: "prepaid", label: "Filter: Prepaid" },
  { value: "clientStock", label: "Filter: Client stock" },
  { value: "clientStockPrepaid", label: "Filter: Client stock · Prepaid" },
  { value: "plain", label: "Filter: Priced" },
];

export function DatasheetListPage() {
  const navigate = useNavigate();
  const { has } = usePermissions();
  const [q, setQ] = useState("");
  const [mode, setMode] = useState("");
  const [typeCode, setTypeCode] = useState("");
  const [page, setPage] = useState(0);

  const { data: types } = useDatasheetTypes();
  const { data, isLoading } = useDatasheets({
    q: q || undefined,
    mode: mode || undefined,
    typeCode: typeCode || undefined,
    page: page + 1,
    pageSize: 50,
  });

  const cols: DataGridColumn<DatasheetRow>[] = [
    { id: "id", header: "Datasheet", accessor: (r) => `#${r.datasheetId}` },
    { id: "company", header: "Company", accessor: (r) => r.companyName ?? "—" },
    { id: "type", header: "Type", accessor: (r) => r.datasheetTypeDescription ?? r.datasheetTypeCode },
    { id: "contact", header: "Primary contact", accessor: (r) => r.primaryContactName ?? "—" },
    {
      id: "mode",
      header: "Mode",
      accessor: (r) => <Badge variant="secondary">{modeLabel(r.mode)}</Badge>,
    },
    { id: "remaining", header: "Remaining", accessor: (r) => String(r.totalRemaining) },
    {
      id: "activity",
      header: "Last activity",
      accessor: (r) => (r.lastActivityUtc ? new Date(r.lastActivityUtc).toLocaleDateString() : "—"),
    },
    {
      id: "warn",
      header: "",
      accessor: (r) =>
        r.hasWarnings ? (
          <span aria-label="Needs attention" title="Balances need attention" className="text-amber-600">
            ●
          </span>
        ) : null,
    },
  ];

  return (
    <ListPage
      title="Datasheets"
      primaryAction={has("datasheet:write") ? (
        <Button size="sm" onClick={() => void navigate("/datasheets/new")}>New datasheet</Button>
      ) : undefined}
      search={{
        value: q,
        onChange: (v) => { setQ(v); setPage(0); },
        placeholder: "Search description / note…",
      }}
      filterBar={
        <div className="flex gap-2">
          <select
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={mode}
            onChange={(e) => { setMode(e.target.value); setPage(0); }}
            aria-label="Filter by mode"
          >
            {MODES.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <select
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={typeCode}
            onChange={(e) => { setTypeCode(e.target.value); setPage(0); }}
            aria-label="Filter by type"
          >
            <option value="">All types</option>
            {(types ?? []).map((t) => (
              <option key={t.datasheetTypeCode} value={t.datasheetTypeCode}>{t.description}</option>
            ))}
          </select>
        </div>
      }
    >
      <DataGrid
        columns={cols}
        rows={data?.items ?? []}
        rowKey={(r) => r.datasheetId}
        totalRows={data?.totalCount ?? 0}
        pageIndex={page}
        pageSize={50}
        loading={isLoading}
        emptyHeadline="No datasheets match these filters."
        onPageChange={setPage}
        onRowClick={(r) => void navigate(`/datasheets/${r.datasheetId}`)}
      />
    </ListPage>
  );
}
